import { CheckOutlineSolid } from "@faire/slate-icons/CheckOutlineSolid";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import { cn } from "../../../lib/utils";
import { EASE_OUT_EXPO, NET_TERMS } from "./shared";
import { SignupStepHeader } from "./SignupStepHeader";

export type LoaderStyle = "checklist" | "ring";

export interface CalculatingScreenProps {
  /** How long the calculation runs before handing off to the reveal. */
  durationMs: number;
  loaderStyle: LoaderStyle;
  onComplete: () => void;
}

const STEPS = NET_TERMS.calculatingSteps;

/**
 * The loading step that "calculates your net terms." It walks a rAF-driven
 * progress value from 0 to 100 over `durationMs`, lighting up each checklist
 * item (or filling the ring) as it goes, then calls `onComplete` to reveal the
 * approved terms. The wait is the point — it makes the number that follows feel
 * earned rather than handed out.
 */
export const CalculatingScreen: React.FC<CalculatingScreenProps> = ({
  durationMs,
  loaderStyle,
  onComplete,
}) => {
  const reduceMotion = useReducedMotion() ?? false;
  const [percent, setPercent] = useState(0);

  // Keep the latest onComplete without making it an animation dependency, so
  // the loop isn't restarted (and progress reset) on every parent re-render.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (reduceMotion) {
      setPercent(100);
      const timeoutId = setTimeout(() => onCompleteRef.current(), 700);
      return () => clearTimeout(timeoutId);
    }

    let rafId = 0;
    let startTs: number | null = null;
    const tick = (now: number): void => {
      if (startTs === null) {
        startTs = now;
      }
      const progress = Math.min((now - startTs) / durationMs, 1);
      setPercent(Math.round(progress * 100));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        onCompleteRef.current();
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [durationMs, reduceMotion]);

  const completedSteps = Math.min(
    STEPS.length,
    Math.floor((percent / 100) * STEPS.length)
  );
  const activeStepLabel =
    STEPS[Math.min(completedSteps, STEPS.length - 1)] ?? STEPS[0];

  // Only the content slides — the header/progress chrome stays put. Slides in
  // from the right and out to the left, always paired with an opacity fade so
  // the entrance and exit read as smooth rather than an abrupt cut. Collapses
  // to a plain crossfade under a reduced-motion preference.
  const slide = !reduceMotion;

  return (
    <div className="bg-fs-surface-primary flex min-h-screen w-full flex-col">
      <SignupStepHeader progressPercent={80 + (percent / 100) * 15} />

      <motion.div
        initial={slide ? { x: "60%", opacity: 0 } : { opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={slide ? { x: "-60%", opacity: 0 } : { opacity: 0 }}
        transition={{ duration: 0.55, ease: EASE_OUT_EXPO }}
        className="mx-auto flex w-full max-w-[560px] flex-1 flex-col items-center justify-center gap-(--slate-spacing-group-xlarge) px-(--slate-spacing-layout-edge-mobile) py-(--slate-spacing-section-large) text-center"
      >
        <div className="flex flex-col items-center gap-(--slate-spacing-group-medium)">
          <h1 className="type-fs-display-s text-fs-text-primary text-balance">
            Calculating your net terms
          </h1>
          <p className="type-fs-paragraph text-fs-text-subdued text-balance">
            Hang tight — we're figuring out how much buying power Faire can put
            behind your store.
          </p>
        </div>

        {loaderStyle === "ring" ? (
          <ProgressRing percent={percent} activeStepLabel={activeStepLabel} />
        ) : (
          <ChecklistProgress completedSteps={completedSteps} />
        )}
      </motion.div>
    </div>
  );
};

const ProgressRing: React.FC<{ percent: number; activeStepLabel: string }> = ({
  percent,
  activeStepLabel,
}) => {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - percent / 100);

  return (
    <div className="flex flex-col items-center gap-(--slate-spacing-group-large)">
      <div className="relative flex size-40 items-center justify-center">
        <svg className="size-full -rotate-90" viewBox="0 0 128 128">
          <circle
            className="text-fs-surface-secondary"
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="10"
          />
          <circle
            className="text-fs-action-surface-default"
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 120ms linear" }}
          />
        </svg>
        <span className="type-fs-display-s text-fs-text-primary absolute">
          {percent}%
        </span>
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={activeStepLabel}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease: EASE_OUT_EXPO }}
          className="type-fs-label-medium text-fs-text-subdued"
        >
          {activeStepLabel}…
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const ChecklistProgress: React.FC<{ completedSteps: number }> = ({
  completedSteps,
}) => {
  return (
    <ul className="flex w-full max-w-[400px] flex-col gap-(--slate-spacing-group-medium)">
      {STEPS.map((step, index) => {
        const isDone = index < completedSteps;
        const isActive = index === completedSteps;
        return (
          <li
            key={step}
            className={cn(
              "flex items-center gap-(--slate-dimensions-3) text-left transition-opacity duration-300",
              !isDone && !isActive && "opacity-40"
            )}
          >
            <StepMarker isDone={isDone} isActive={isActive} />
            <span
              className={cn(
                "type-fs-paragraph-medium",
                isDone ? "text-fs-text-primary" : "text-fs-text-subdued"
              )}
            >
              {step}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const StepMarker: React.FC<{ isDone: boolean; isActive: boolean }> = ({
  isDone,
  isActive,
}) => {
  if (isDone) {
    return (
      <span className="bg-fs-message-surface-success flex size-6 shrink-0 items-center justify-center rounded-full">
        <CheckOutlineSolid className="text-fs-icon-success size-4" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full border-2",
        isActive
          ? "border-fs-action-surface-default"
          : "border-fs-border-subdued"
      )}
    >
      {isActive ? (
        <motion.span
          className="bg-fs-action-surface-default size-2.5 rounded-full"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
        />
      ) : null}
    </span>
  );
};
